// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Styles, StylesProvider } from "@insite/content-library/additionalStyles";
import Clickable from "@insite/mobius/Clickable";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries, getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xHeaderUpdate from "@keller/App/Handlers/XNG/Header";
import useSize, { useElementSize } from "@keller/App/Hooks/useSize";
import { CustomAppState } from "@keller/App/Store/Reducers";
import {
    getHeaderSearchInputWidget,
    getLogoImageWidget,
    getMainNavigationWidget,
    getMaintenanceMessageWidget,
} from "@keller/App/Store/Selectors/GeneralSelectors";
import Close from "@keller/XDL/Icons/Close";
import Menu from "@keller/XDL/Icons/Menu";
import { color, layoutContainer, xClassNames } from "@keller/XDL/Utilities";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const headerActionsWidgets = getWidgetsByPageId(state, getHeader(state).id)
        .filter(widget => widget.zone === "Actions")
        .map(widget => ({
            id: widget.id,
            type: widget.type,
            fields: widget.fields,
        }));

    return {
        logoImageWidget: getLogoImageWidget(state),
        headerSearchInputWidget: getHeaderSearchInputWidget(state),
        mainNavigationWidget: getMainNavigationWidget(state),
        headerActionsWidgets,
        maintenanceMessageWidget: getMaintenanceMessageWidget(state),
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    xHeaderUpdate,
};

type Props = PageProps &
    HasShellContext &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CompactHeaderStyles {
    page?: MobiusPageProps;
    mainNavigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    pageInnerWrapper?: InjectableCss;
    /**
     * XNG-Changes
     */
    pageOuterWrapper?: InjectableCss;
}

const boxShadowStyleMap: Dictionary<string> = {
    light: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
    medium: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    dark: "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
};

function generateShadow(props: ThemeProps<BaseTheme>) {
    const { isDropShadow, dropShadowStyle } = props.theme.header;
    if (isDropShadow && dropShadowStyle in boxShadowStyleMap) {
        return `box-shadow: ${boxShadowStyleMap[dropShadowStyle]}`;
    }
    return "";
}

function generateBorder(props: ThemeProps<BaseTheme>) {
    const { isBorder, borderColor, borderThickness, isBorderTop, isBorderLeft, isBorderBottom, isBorderRight } =
        props.theme.header;
    if (!isBorder) {
        return "border: none;";
    }

    let border = `border: ${borderThickness}px solid ${resolveColor(borderColor, props.theme)};`;

    if (!isBorderTop) {
        border += "border-top: none;";
    }
    if (!isBorderBottom) {
        border += "border-bottom: none;";
    }
    if (!isBorderLeft) {
        border += "border-left: none;";
    }
    if (!isBorderRight) {
        border += "border-right: none;";
    }

    return border;
}

export const compactHeaderStyles: CompactHeaderStyles = {
    pageInnerWrapper: {
        css: css`
            gap: 20px;
        `,
    },
    page: {
        css: css`
            position: fixed;
            top: 0;
            left: 0;
            z-index: ${getProp("theme.zIndex.compactHeader")};
            box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.25);
            opacity: 0;
            transform: translateY(-100%) translateX(0);
            padding: ${props => props.theme.header.padding};
            width: 100%;
            &&&& {
                position: fixed;
            }
            ${generateBorder}
            ${generateShadow};
        `,
    },
    pageOuterWrapper: {
        css: css`
            background: ${color("common.white")};
        `,
    },
    logoWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 20px;
            > *:first-child {
                margin-right: 10px;
            }
            > div:nth-child(2) {
                text-align: right;
                & > div.wrapper {
                    width: auto;
                }
            }
        `,
    },
    searchWrapper: {
        css: css`
            align-self: center;
            flex-grow: 1;
            max-width: 695px;
            margin: 0 20px;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 20px;
            justify-content: flex-end;
        `,
    },
    mainNavigationWrapper: {
        css: css`
            position: absolute;
            background: ${color("primary")};
            width: 100%;
            top: -100px;
            transition: top 500ms ease-in-out;
            z-index: -1;
            ${({ showMainNavigation }: any) =>
                showMainNavigation &&
                css`
                    top: 100%;
                `}
            > div {
                ${layoutContainer}
            }
        `,
    },
};

const styles = compactHeaderStyles;

export const createStylesProviderStyles = (theme: BaseTheme) =>
    ({
        mainNavigation: {
            menuItemTypography: {
                css: css`
                    font-weight: 600;
                    white-space: nowrap;
                `,
                color: theme.header.linkColor,
            },
        },
        logo: {
            img: {
                css: css`
                    width: 120px;
                    height: 48px;
                `,
            },
        },
        headerSearchInput: {
            searchInputStyles: {
                popoverContentBody: {
                    as: "div",
                    _height: "auto",
                    css: css`
                        display: inline-flex;
                        justify-content: flex-end;
                        border-radius: 3px;
                    `,
                },
                searchHistoryStyles: {
                    wrapper: {
                        css: css`
                            display: flex;
                            flex-direction: column;
                            width: 240px;
                            padding: 0 20px 15px 20px;
                        `,
                    },
                },
            },
        },
    }) as Styles;

const transitionStyles: Dictionary<{ opacity: number; transform: string }> = {
    entering: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    entered: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exiting: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exited: { opacity: 0, transform: "translateY(-100%) translateX(0)" },
};

// XNG-Changes - Custom Style
const InnerWrapper = styled.div<{ isEdit?: boolean } & InjectableCss>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: ${props => (props.isEdit ? "30px 10px" : "16px 20px")};
    width: 100%;
    justify-content: space-between;
    margin: 0 auto;
    ${({ theme, isEdit }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return isEdit
            ? css`
                  max-width: 100%;
              `
            : breakpointMediaQueries(
                  theme,
                  maxWidths.map((mw: any) =>
                      mw
                          ? css`
                                max-width: ${mw}px;
                            `
                          : null,
                  ),
              );
    }}
    ${injectCss}
`;

const CompactHeader = (props: Props) => {
    const { isCurrentPage, isEditing, isInShell } = props.shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const [isShow, setIsShow] = useState(false);
    const transitionElement = React.useRef<HTMLElement | null>(null);
    const [showMainNavigation, setShowMainNavigation] = useState(false);
    const headerWidth = typeof document !== "undefined" ? useElementSize(document.body)?.width : "100%";
    // <XNG-Changes>
    const stickyHeaderHeight = useSize(transitionElement)?.height;

    useEffect(() => {
        if (stickyHeaderHeight) {
            props.xHeaderUpdate({ key: "stickyHeight", value: stickyHeaderHeight });
        }
    }, [stickyHeaderHeight]);
    // </XNG-Changes>

    useEffect(() => {
        function eventHandler() {
            const scrollPos = Math.round(window.scrollY);
            // const windowWidth = Math.round(window.innerWidth);
            let isSticky = false;
            // XNG-Changes - Remove windowWidth > 767
            // if (scrollPos > Number(props.theme.header.triggerPoint) && windowWidth > 767) {
            if (scrollPos > Number(props.theme.header.triggerPoint)) {
                setIsShow(true);
                isSticky = true;
                // XNG-Changes - remove || windowWidth < 768
                // } else if ((isShow && scrollPos < Number(props.theme.header.triggerPoint)) || windowWidth < 768) {
            } else if (isShow && scrollPos < Number(props.theme.header.triggerPoint) + 150) {
                setIsShow(false);
                isSticky = false;
            }

            props.xHeaderUpdate({ key: "isSticky", value: isSticky });
        }
        window.addEventListener("scroll", eventHandler, { passive: true });

        return () => {
            window.removeEventListener("scroll", eventHandler);
        };
    }, [props.theme.header.triggerPoint, isShow]);

    useEffect(() => {
        setIsShow(Boolean(isShell));
    }, [isShell]);

    const openNavigationDrawer = () => {
        setShowMainNavigation(prevState => !prevState);
    };

    // XNG-Changes: structure
    if (props.isTabletScreen) {
        return null;
    }

    return (
        <Transition nodeRef={transitionElement} in={isShow} timeout={0}>
            {state => (
                <StyledWrapper
                    ref={transitionElement}
                    {...styles.page}
                    as="header"
                    background={props?.theme?.header?.backgroundColor}
                    id="compact-header"
                    data-test-selector="compact-header"
                    className={xClassNames.append(isShow ? "shown-compact-header" : "hidden-compact-header")}
                    style={{
                        transition: "all 350ms ease",
                        pointerEvents: isShow ? "auto" : "none",
                        ...transitionStyles[state],
                        maxWidth: headerWidth,
                    }}
                >
                    <StylesProvider styles={createStylesProviderStyles(props.theme)}>
                        {createWidgetElement("MaintenanceMessage/MaintenanceMessage", {
                            fields: {
                                ...props.maintenanceMessageWidget,
                            },
                        })}
                        <StyledWrapper {...styles.pageOuterWrapper}>
                            <InnerWrapper isEdit={isEdit} {...styles.pageInnerWrapper}>
                                <StyledWrapper {...styles.logoWrapper}>
                                    {props.isTabletScreen ? (
                                        createWidgetElement("Header/MainNavigation", {
                                            fields: { ...props.mainNavigationWidget },
                                        })
                                    ) : (
                                        <Clickable
                                            onClick={openNavigationDrawer}
                                            data-test-selector="expandMobileMenu"
                                            aria-label="main-navigation"
                                        >
                                            <Icon src={showMainNavigation ? Close : Menu} color="primary" />
                                        </Clickable>
                                    )}
                                    {createWidgetElement("Basic/Logo", {
                                        fields: {
                                            mobileSpecificImage: props.logoImageWidget?.logoImage,
                                            logoImage: props.logoImageWidget?.logoImage,
                                        },
                                    })}
                                </StyledWrapper>
                                <StyledWrapper {...styles.searchWrapper}>
                                    {createWidgetElement("Header/HeaderSearchInput", {
                                        fields: {
                                            ...props.headerSearchInputWidget,
                                        },
                                    })}
                                </StyledWrapper>
                                <StyledWrapper {...styles.actionsWrapper}>
                                    {props.headerActionsWidgets.map(widget => (
                                        <React.Fragment key={widget.id}>
                                            {createWidgetElement(widget.type, {
                                                fields: {
                                                    ...widget.fields,
                                                },
                                            })}
                                        </React.Fragment>
                                    ))}
                                </StyledWrapper>
                            </InnerWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...{ showMainNavigation }} {...styles.mainNavigationWrapper}>
                            {createWidgetElement("Header/MainNavigation", {
                                fields: { ...props.mainNavigationWidget },
                            })}
                        </StyledWrapper>
                    </StylesProvider>
                </StyledWrapper>
            )}
        </Transition>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withIsInShell(CompactHeader))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
